import React, { Fragment, useEffect, useRef, useState } from 'react';
import { MailViewerProps } from "./MailViewer.props";
import Iframe from "../IFrame/IFrame";
import Skeleton from "@mui/material/Skeleton";
import './MailViewer.styles.css';
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import {Modal} from "../../atoms/Modal";
import { useTranslation } from "react-i18next";
import {truncateText } from "../../../utils/common";
function isAllowedUrl(rawHref: string): boolean {
    try {
        const url = new URL(rawHref, window.location.origin);

        // Only allow safe protocols (adjust if needed)
        const allowedProtocols = new Set(["http:", "https:", "mailto:"]);
        if (!allowedProtocols.has(url.protocol)) return false;

        // Optional: block local hosts
        const blockedHosts = new Set(["localhost", "127.0.0.1"]);
        if (blockedHosts.has(url.hostname)) return false;

        return true;
    } catch {
        return false;
    }
}

function openSafely(url: string) {
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (w) w.opener = null;
}

export const MailViewer: React.FC<MailViewerProps> = ({ content, image, subject }) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [pendingUrl, setPendingUrl] = useState<string | null>(null);
    const [contentModal, setContentModal] = useState<any>({});

    const { t } = useTranslation()

    useEffect(() => {
        if (!content) return;

        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        // Find the iframe rendered by <Iframe />
        const iframe = wrapper.querySelector("iframe") as HTMLIFrameElement | null;
        if (!iframe) return;

        let cleanupDocListener: (() => void) | null = null;

        const attach = () => {
            let doc: Document | null = null;

            try {
                // Works only if iframe is same-origin or srcDoc accessible
                doc = iframe.contentDocument || iframe.contentWindow?.document || null;
            } catch {
                // Cross-origin => cannot access DOM to add listener
                return;
            }
            if (!doc) return;

            const clickHandler = (e: MouseEvent) => {
                const target = e.target as HTMLElement | null;
                const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
                if (!anchor) return;

                e.preventDefault();
                e.stopPropagation();

                const href = anchor.getAttribute("href") || "";
                if (!isAllowedUrl(href)) {
                    console.warn("Blocked unsafe URL:", href);
                    return;
                }

                const html = <Fragment>
                    <Typography variant="body2"
                                dangerouslySetInnerHTML={{__html: t("openExternalLink.description",
                                        {email_subject: truncateText(subject, 60)})}
                    } >

                    </Typography>
                    <Box className={"opened-external-link-container"}>
                        <Typography
                            variant="body2"
                            className={"opened-external-link notes "}
                        >
                            <div>
                                {truncateText(href, 120)}
                            </div>
                        </Typography>
                    </Box>
                </Fragment>

                const title = t("openExternalLink.title")
                const textAgree = t("openExternalLink.textAgree")
                const textCancel = t("openExternalLink.textCancel")
                const variant = 'success'

                generateConfirmContentModal(html, title, textAgree, textCancel, variant)
                setPendingUrl(href);
            };

            // capture=true to intercept early
            doc.addEventListener("click", clickHandler, true);

            cleanupDocListener = () => {
                doc?.removeEventListener("click", clickHandler, true);
            };
        };

        const onLoad = () => attach();

        // Attach on load (important)
        iframe.addEventListener("load", onLoad);

        // If already loaded, try attach immediately
        // (avoid missing the first click if the iframe loads very fast)
        try {
            if (iframe.contentDocument?.readyState === "complete") {
                attach();
            }
        } catch {
            // ignore cross-origin
        }

        return () => {
            iframe.removeEventListener("load", onLoad);
            cleanupDocListener?.();
        };
    }, [content]);

    const generateConfirmContentModal = (element: any, title: string, textAgree: string, textCancel: string, variant: string, params?: any) => {
        const content:any = {
            title: title,
            content: element,
            params: params,
            textAgree: textAgree,
            textCancel: textCancel,
            variant: variant,
            icon: null
        }
        setContentModal(content)
    }

    return (
        <Fragment>
            {
                content == undefined ?
                    (<Skeleton variant="rounded" width={'100%'} height={60} />)
                    :
                    (
                        <div ref={wrapperRef}>
                            <Iframe content={content} image={image} />
                        </div>
                    )
            }

            <Modal
                idModal='modal-open-external-link'
                showIcon={false}
                openDialog={!!pendingUrl}
                variant={contentModal.variant}
                title={contentModal.title}
                content={contentModal.content}
                textAgree={contentModal.textAgree}
                textCancel={contentModal.textCancel}
                actionAgree={() => {
                    if (pendingUrl) openSafely(pendingUrl);
                    setPendingUrl(null);
                }}
                actionCancel={() => setPendingUrl(null)}
                onClose={() => setPendingUrl(null)}
            />
        </Fragment>
    );
};